import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate } from "@/lib/api";
import { Inbox, Filter, ChevronDown, Loader2, Mail, Clock, CheckCircle2, X, Trash2 } from "lucide-react";

const STATUS_OPTIONS = [
    { value: "open", label: "Aperta", icon: Inbox, tone: "vermillion" },
    { value: "in_progress", label: "In lavorazione", icon: Clock, tone: "magenta" },
    { value: "answered", label: "Risposta inviata", icon: CheckCircle2, tone: "emerald" },
];

const toneClass = (tone) => {
    switch (tone) {
        case "vermillion": return "text-vermillion border-vermillion/40 bg-vermillion/10";
        case "magenta": return "text-magenta border-magenta/40 bg-magenta/10";
        case "emerald": return "text-emerald-300 border-emerald-400/30 bg-emerald-500/10";
        default: return "text-zinc-400 border-white/10 bg-white/5";
    }
};

export default function AdminChatbot() {
    const [requests, setRequests] = useState([]);
    const [stats, setStats] = useState(null);
    const [filter, setFilter] = useState("");
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const params = filter ? { params: { status: filter } } : {};
        const [reqs, s] = await Promise.all([
            apiPrivate.get("/admin/chatbot/requests", params),
            apiPrivate.get("/admin/chatbot/stats"),
        ]);
        setRequests(reqs.data || []);
        setStats(s.data);
        setLoading(false);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const updateStatus = async (id, status) => {
        await apiPrivate.patch(`/admin/chatbot/requests/${id}`, { status });
        load();
        if (selected?.id === id) setSelected(s => ({ ...s, status }));
    };

    const updateNote = async (id, admin_note) => {
        const r = await apiPrivate.patch(`/admin/chatbot/requests/${id}`, { admin_note });
        if (selected?.id === id) setSelected(r.data);
        // Refresh list silently
        load();
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminare definitivamente questa richiesta? L'operazione non è reversibile.")) return;
        await apiPrivate.delete(`/admin/chatbot/requests/${id}`);
        setSelected(null);
        load();
    };

    return (
        <AppLayout admin>
            <div className="max-w-[1400px] mx-auto" data-testid="admin-chatbot">
                <div>
                    <span className="label-tag text-magenta">Chatbot</span>
                    <h1 className="font-display text-5xl mt-3 tracking-tighter">
                        Richieste <span className="font-serif-italic text-gradient-tropical">soci</span>.
                    </h1>
                    <p className="text-zinc-400 mt-3 max-w-2xl">
                        Domande che il chatbot non ha potuto risolvere automaticamente con le FAQ e che sono state inoltrate al team Investor Relations.
                    </p>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8" data-testid="chatbot-stats">
                        <StatCard label="Totale" value={stats.total} tone="default" />
                        <StatCard label="Aperte" value={stats.open} tone="vermillion" />
                        <StatCard label="In lavorazione" value={stats.in_progress} tone="magenta" />
                        <StatCard label="Risposte" value={stats.answered} tone="emerald" />
                    </div>
                )}

                {/* Filter bar */}
                <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="chatbot-filters">
                    <Filter className="h-4 w-4 text-zinc-500" />
                    <FilterPill active={filter === ""} onClick={() => setFilter("")} testid="filter-all">Tutte</FilterPill>
                    {STATUS_OPTIONS.map(o => (
                        <FilterPill key={o.value} active={filter === o.value} onClick={() => setFilter(o.value)} testid={`filter-${o.value}`} tone={o.tone}>
                            {o.label}
                        </FilterPill>
                    ))}
                </div>

                {/* List */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
                    {loading ? (
                        <div className="col-span-full bento p-12 text-center text-zinc-500" data-testid="chatbot-loading">
                            <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Caricamento richieste…
                        </div>
                    ) : requests.length === 0 ? (
                        <div className="col-span-full bento p-12 text-center text-zinc-500" data-testid="chatbot-empty">
                            <Mail className="h-10 w-10 mx-auto mb-3 opacity-40" />
                            Nessuna richiesta da gestire.
                        </div>
                    ) : (
                        requests.map(r => (
                            <button
                                key={r.id}
                                onClick={() => setSelected(r)}
                                data-testid={`chatbot-request-${r.id}`}
                                className="bento p-5 text-left transition hover:border-bayside/40"
                            >
                                <div className="flex items-start justify-between gap-3 mb-3">
                                    <StatusBadge status={r.status} />
                                    <span className="text-xs font-mono text-zinc-500">{r.ref_number}</span>
                                </div>
                                <div className="text-white font-display text-lg leading-snug line-clamp-2 mb-3">{r.question}</div>
                                <div className="flex items-center justify-between gap-2 text-xs font-grotesk text-zinc-500">
                                    <span className="truncate">
                                        {r.user_ragione_sociale || `${r.user_nome || ""} ${r.user_cognome || ""}`.trim() || r.user_email}
                                    </span>
                                    <span>{formatDate(r.created_at)}</span>
                                </div>
                            </button>
                        ))
                    )}
                </div>

                {selected && (
                    <RequestModal
                        request={selected}
                        onClose={() => setSelected(null)}
                        onStatusChange={(s) => updateStatus(selected.id, s)}
                        onNoteSave={(n) => updateNote(selected.id, n)}
                        onDelete={() => remove(selected.id)}
                    />
                )}
            </div>
        </AppLayout>
    );
}

function StatCard({ label, value, tone }) {
    return (
        <div className={`bento p-4 ${tone !== "default" ? "border-l-4" : ""} ${tone === "vermillion" ? "border-l-vermillion" : tone === "magenta" ? "border-l-magenta" : tone === "emerald" ? "border-l-emerald-400" : ""}`} data-testid={`stat-${tone}`}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">{label}</div>
            <div className="font-display text-3xl mt-1">{value}</div>
        </div>
    );
}

function FilterPill({ active, onClick, children, testid, tone }) {
    const inactive = "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white";
    const activeClass = tone ? toneClass(tone) : "border-bayside text-white bg-bayside/15";
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`text-xs uppercase tracking-widest font-grotesk px-3 py-2 rounded-full border transition ${active ? activeClass : inactive}`}
        >
            {children}
        </button>
    );
}

function StatusBadge({ status }) {
    const opt = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
    const Icon = opt.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-grotesk px-2.5 py-1 rounded-full border ${toneClass(opt.tone)}`} data-testid={`status-badge-${status}`}>
            <Icon className="h-3 w-3" /> {opt.label}
        </span>
    );
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
}

function RequestModal({ request, onClose, onStatusChange, onNoteSave, onDelete }) {
    const [note, setNote] = useState(request.admin_note || "");
    const [saving, setSaving] = useState(false);
    const quoteLabel = (request.user_quota_categories || []).map(q => `Quota ${q}`).join(", ") || "—";
    const displayName = request.user_ragione_sociale || `${request.user_nome || ""} ${request.user_cognome || ""}`.trim() || request.user_email;

    const save = async () => {
        setSaving(true);
        await onNoteSave(note);
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="chatbot-modal">
            <div className="bg-[#0a0c1c] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-8">
                    <div className="flex items-start justify-between gap-4 mb-6">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                                <StatusBadge status={request.status} />
                                <span className="text-xs font-mono text-zinc-500">{request.ref_number}</span>
                            </div>
                            <h2 className="font-display text-3xl tracking-tighter leading-tight">Richiesta socio.</h2>
                        </div>
                        <button onClick={onClose} data-testid="chatbot-modal-close" className="text-zinc-500 hover:text-white">
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Socio info */}
                    <div className="grid grid-cols-2 gap-3 text-sm bg-white/5 border border-white/10 rounded-2xl p-4 mb-5">
                        <Info label="Socio" value={displayName} />
                        <Info label="Email" value={<a href={`mailto:${request.user_email}`} className="text-bayside hover:underline">{request.user_email}</a>} />
                        <Info label="Quote" value={quoteLabel} />
                        <Info label="Inviata" value={formatDate(request.created_at)} />
                    </div>

                    {/* Question */}
                    <div className="bg-[#01172d] border-l-4 border-bayside rounded-r-2xl p-5 mb-6" data-testid="modal-question">
                        <div className="text-[10px] uppercase tracking-widest text-bayside font-grotesk mb-2">Domanda</div>
                        <div className="text-white text-base leading-relaxed whitespace-pre-wrap">{request.question}</div>
                    </div>

                    {/* Status workflow */}
                    <div className="mb-6">
                        <div className="label-tag mb-3">Aggiorna stato</div>
                        <div className="flex flex-wrap gap-2">
                            {STATUS_OPTIONS.map(o => {
                                const Icon = o.icon;
                                const active = request.status === o.value;
                                return (
                                    <button
                                        key={o.value}
                                        onClick={() => onStatusChange(o.value)}
                                        disabled={active}
                                        data-testid={`modal-status-${o.value}`}
                                        className={`inline-flex items-center gap-2 text-xs uppercase tracking-widest font-grotesk px-3 py-2 rounded-full border transition ${
                                            active ? toneClass(o.tone) + " opacity-100" : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                                        }`}
                                    >
                                        <Icon className="h-3.5 w-3.5" /> {o.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Internal note */}
                    <div className="mb-6">
                        <div className="label-tag mb-2">Nota interna (visibile solo agli admin)</div>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={4}
                            data-testid="modal-note-input"
                            placeholder="Es. risposta inviata il 28/05 via email, in attesa di approvazione legale, ecc."
                            className="w-full bg-[#0F0F0F] border border-white/10 text-white p-3 font-grotesk text-sm focus:outline-none focus:border-bayside resize-none rounded-2xl"
                        />
                        <div className="flex items-center justify-between mt-3 gap-3">
                            <a
                                href={`mailto:${request.user_email}?subject=Re: la tua richiesta ${request.ref_number}&body=Ciao ${request.user_nome || ""},%0A%0Ain risposta alla tua richiesta del ${formatDate(request.created_at)} (rif. ${request.ref_number}):%0A%0A`}
                                data-testid="modal-reply-email"
                                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-grotesk text-bayside hover:text-white border border-bayside/40 hover:bg-bayside/10 px-3 py-2 rounded-full transition"
                            >
                                <Mail className="h-3.5 w-3.5" /> Rispondi via email
                            </a>
                            <div className="flex gap-2">
                                <button
                                    onClick={onDelete}
                                    data-testid="modal-delete"
                                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-grotesk text-zinc-500 hover:text-vermillion border border-white/10 hover:border-vermillion/40 px-3 py-2 rounded-full transition"
                                >
                                    <Trash2 className="h-3.5 w-3.5" /> Elimina
                                </button>
                                <button
                                    onClick={save}
                                    disabled={saving || note === (request.admin_note || "")}
                                    data-testid="modal-save-note"
                                    className="btn-rebel"
                                >
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva nota"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Info({ label, value }) {
    return (
        <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">{label}</div>
            <div className="text-sm text-white truncate mt-1">{value || "—"}</div>
        </div>
    );
}
